import { useEffect, useRef, useState } from 'react';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  /** If provided, user must type this exact string before the confirm button is enabled */
  requireConfirmText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Delete',
  requireConfirmText,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [typedText, setTypedText] = useState('');

  const confirmDisabled = requireConfirmText !== undefined && typedText !== requireConfirmText;

  // Focus cancel button on open; close on Escape
  useEffect(() => {
    cancelRef.current?.focus();
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div className={styles.backdrop} onClick={onCancel} role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <p id="confirm-title" className={styles.title}>{title}</p>
        <p className={styles.message}>{message}</p>
        {requireConfirmText !== undefined && (
          <div className={styles.confirmInputWrapper}>
            <label className={styles.confirmInputLabel}>
              Type <strong>{requireConfirmText}</strong> to confirm
            </label>
            <input
              className={styles.confirmInput}
              type="text"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              placeholder={requireConfirmText}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
        <div className={styles.actions}>
          <button ref={cancelRef} className={styles.btnCancel} onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`${styles.btnConfirm} ${confirmDisabled ? styles.btnConfirmDisabled : ''}`}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
